import React, { type ReactNode, forwardRef } from 'react'

import type { Variant } from '../../../themes/types'
import { withTooltip } from '../../../utils'
import type { IconName } from '../Icon/Icon'
import type { HeightType, Sticky, Type } from './Button.styles'
import StyledButtonContainer, {
  StyledButtonIcon,
  StyledButtonText,
  StyledIconContainer,
} from './Button.styles'

export interface ButtonProps {
  'aria-label'?: string
  'data-e2e'?: string
  children?: ReactNode
  className?: string
  disabled?: boolean
  fluid?: boolean
  form?: string
  height?: HeightType
  icon?: IconName
  iconOutline?: boolean
  onClick?: (event?: React.MouseEvent) => void
  outline?: boolean
  reversed?: boolean
  sticky?: Sticky
  text?: boolean
  type?: Type
  underline?: boolean
  variant: Variant
  title?: string
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      'aria-label': ariaLabel,
      'data-e2e': dataE2e,
      children,
      className,
      disabled = false,
      fluid = false,
      form,
      height = 'default',
      icon,
      iconOutline = false,
      onClick,
      outline = false,
      reversed = false,
      sticky,
      text = false,
      title,
      type = 'button',
      underline = false,
      variant,
      ...props
    },
    ref
  ) => (
    <StyledButtonContainer
      $reversed={reversed}
      aria-label={ariaLabel}
      className={className}
      data-e2e={dataE2e}
      disabled={disabled}
      fluid={fluid}
      form={form}
      hasChildren={!!children}
      height={height}
      onClick={onClick}
      outline={outline}
      sticky={sticky}
      text={text}
      title={title}
      type={type}
      ref={ref}
      variant={variant}
      {...props}
    >
      {!!icon && (
        <StyledIconContainer>
          <StyledButtonIcon
            name={icon}
            faStyle={iconOutline ? 'far' : 'fas'}
            iconVariant={variant}
            isOutline={outline || text}
          />
        </StyledIconContainer>
      )}
      {children && (
        <StyledButtonText data-e2e="inner-button" underline={underline}>
          {children}
        </StyledButtonText>
      )}
    </StyledButtonContainer>
  )
)

export default withTooltip(Button)
